import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/SectionHeading";

const TierCard = ({ tier, index, mostPopular }) => {
  const hl = tier.highlighted;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      data-testid={`pricing-tier-${tier.id}`}
      className={`relative flex flex-col rounded-[2rem] border p-7 sm:p-8 ${
        hl
          ? "border-[var(--olive)] bg-gradient-to-b from-[#1b2119] to-[var(--surface)] shadow-[0_40px_90px_-40px_rgba(127,121,57,0.7)] lg:-my-4 lg:py-12"
          : "border-[var(--line)] bg-[var(--surface)]"
      }`}
    >
      {hl && (
        <span className="absolute -top-3.5 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-[var(--olive)] px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white">
          <Star className="h-3 w-3 fill-white" />
          {mostPopular}
        </span>
      )}

      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-3xl text-[var(--ink)]">{tier.name}</h3>
        <span className="font-mono text-[11px] text-[var(--ink-3)]">0{index + 1}</span>
      </div>
      <p className="mt-1.5 text-sm text-[var(--ink-2)]">{tier.tagline}</p>

      <div className="mt-8">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1.5">
          <span className="font-display text-4xl leading-none text-[var(--ink)] sm:text-5xl">{tier.price}</span>
          <span className="text-sm leading-snug text-[var(--ink-2)]">{tier.priceNote}</span>
        </div>
        {tier.altPrice && (
          <div className="mt-2 text-sm text-[var(--ink-2)]">
            <span className="font-semibold text-[var(--ink)]">{tier.altPrice}</span> {tier.altPriceNote}
          </div>
        )}
        {tier.trial && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[var(--sage)]/40 bg-[var(--olive)]/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-[var(--sage)]">
            {tier.trial}
          </div>
        )}
        {tier.note && <p className="mt-4 text-xs leading-relaxed text-[var(--ink-2)]">{tier.note}</p>}
      </div>

      <a
        href="#download"
        data-testid={`pricing-${tier.id}-cta`}
        className={`btn mt-8 w-full px-6 py-3.5 text-sm ${hl ? "btn-primary" : "btn-ghost"}`}
      >
        {tier.cta}
      </a>

      {tier.cancel && <p className="mt-3 text-center text-[11px] leading-snug text-[var(--ink-3)]">{tier.cancel}</p>}

      <ul className="mt-8 space-y-3.5 border-t border-[var(--line)] pt-8">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-3">
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${hl ? "text-[var(--sage)]" : "text-[var(--ink-3)]"}`} />
            <span className="text-sm text-[var(--ink)]/90">{f}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

export const Pricing = () => {
  const { t } = useLanguage();
  const s = t.pricing;

  return (
    <section id="preise" className="relative scroll-mt-24 py-24 md:py-32">
      <div className="hairline absolute inset-x-0 top-0 mx-auto max-w-5xl" />
      <div className="mx-auto max-w-7xl px-5 sm:px-6 md:px-8">
        <SectionHeading index={s.index} eyebrow={s.eyebrow} title={s.title} body={s.subtitle} align="center" />
        <div className="mx-auto mt-16 grid max-w-5xl items-start gap-5 lg:grid-cols-3 lg:gap-6">
          {s.tiers.map((tier, i) => (
            <TierCard key={tier.id} tier={tier} index={i} mostPopular={s.mostPopular} />
          ))}
        </div>
      </div>
    </section>
  );
};
